import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "devextreme-react/data-grid";
import { FilterTabs, MasterGrid, MasterPage, OpenAction, OPEN_COLUMN, StaffSelectBox } from "../Shared/master";
import DateRangePicker from "../Shared/DateRangePicker";
import useAbortSignal from "../../hooks/useAbortSignal";
import { isRequestCancelled } from "../../apis/useAxios";
import { showErrorToast } from "../../helpers/message";
import { usePermission } from "../../helpers/permissions";
import FeedbackDrawer from "./FeedbackDrawer";
import { StatusChip } from "./ConversationThread";
import {
  FEEDBACK_TYPES,
  FILTERS,
  MENU_CODE,
  RANGE_PRESETS,
  defaultRange,
  filterRows,
  formatDateTime,
  isClosed,
  lastActivity,
  needsReply,
  searchError,
  statusOf,
  typeLabel,
} from "./feedbackRules";
import { getFeedbacks } from "./feedbackService";
import "./feedback.css";
import "./feedbackConversation.css";

const renderType = ({ data }) => (
  <span className="fbk-type">
    <i className={FEEDBACK_TYPES[data.feedbackType]?.icon ?? "fas fa-comment-dots"} aria-hidden="true" /> {typeLabel(data)}
  </span>
);
const renderCrew = ({ data }) => (
  <span>
    {data.crewName || "-"}
    {data.employeeNumber && <span className="text-muted"> ({data.employeeNumber})</span>}
  </span>
);
const renderIssue = ({ data }) => (
  <span className="fbk-issue">
    {data.issueType || "-"}
    {data.priority && <span className={`fbk-priority is-p${data.priorityId}`}>{data.priority}</span>}
  </span>
);
const renderText = ({ data }) => (
  <span className="fbk-clamp" title={data.description}>
    {data.description}
  </span>
);
const renderStatus = ({ data }) => (
  <span className="fbk-status-cell">
    <StatusChip statusId={data.statusId} />
    {needsReply(data) && <i className="fas fa-circle fbk-dot" title="Needs reply" aria-label="Needs reply" />}
  </span>
);

/**
 * Crew Feedback › Feedback: what crew report from the crew app about hotels, hotel shuttles and local vendors, searched by
 * date range, crew member or both. Open a feedback to read its details, photos and conversation; with Edit rights, reply and set its status
 * (the crew member sees both in the app).
 */
export function FeedbackComponent() {
  const getSignal = useAbortSignal();
  const { edit: canEdit } = usePermission(MENU_CODE);
  // What the fields show (draft) and what was searched; Search copies the draft (a new object always reloads).
  const [draft, setDraft] = useState(() => ({ range: defaultRange(), crew: "" }));
  const [search, setSearch] = useState(draft);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    if (searchError(search)) return;
    const [from, to] = search.range;
    setLoading(true);
    try {
      setRows(await getFeedbacks({ from, to, employeeNumber: search.crew }, { signal: getSignal() }));
    } catch (err) {
      if (!isRequestCancelled(err)) showErrorToast(err?.message);
    } finally {
      setLoading(false);
    }
  }, [search, getSignal]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, filterRows(rows, f.id).length])), [rows]);
  const shown = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const open = useMemo(() => shown.filter((r) => !isClosed(r)).length, [shown]);
  const draftError = searchError(draft);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const renderOpen = useCallback(({ data }) => <OpenAction onClick={() => setSelectedId(data.id)} title="Open feedback (details, conversation, reply)" />, []);

  return (
    <MasterPage title="Feedback">
      <section className="mst-filter-panel" aria-label="Filters">
        <FilterTabs
          wide
          ariaLabel="Filter feedback"
          items={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))}
          value={filter}
          total={counts.all}
          onChange={setFilter}
        />
        <div className="mst-filter-panel__bar">
          <div className="mst-filter-panel__field mst-filter-panel__field--narrow">
            <label className="mst-filter-panel__label">Sent from - to</label>
            <DateRangePicker
              startDate={draft.range[0]}
              endDate={draft.range[1]}
              presets={RANGE_PRESETS}
              isClearable
              ariaLabel="Sent from - to"
              onChange={(next) => setDraft((d) => ({ ...d, range: next ?? [null, null] }))}
            />
          </div>
          <div className="mst-filter-panel__field mst-filter-panel__field--narrow">
            <label className="mst-filter-panel__label">Crew member</label>
            <StaffSelectBox value={draft.crew} onChange={(employeeNumber) => setDraft((d) => ({ ...d, crew: employeeNumber ?? "" }))} placeholder="All crew" />
          </div>
          {/* Dates, crew member or both; a new object always reloads, so the same search refreshes. */}
          <button
            type="button"
            className="btn btn-primary btn-sm mst-filter-panel__search"
            title={draftError || "Search"}
            disabled={loading || Boolean(draftError)}
            onClick={() => setSearch({ ...draft })}
          >
            <i className={`fas fa-${loading ? "spinner fa-spin" : "search"}`} aria-hidden="true" /> Search
          </button>
          <p className="mst-filter-panel__summary" aria-live="polite">
            <span className="fbk-sum is-open">{open} open</span>
            <span className="fbk-sum is-reply">{counts.needsReply} need a reply</span>
          </p>
        </div>
        <p className={`fbk-hint${draftError ? " is-error" : ""}`}>
          <i className="fas fa-info-circle" aria-hidden="true" />{" "}
          {draftError || "Pick a date range, a crew member, or both. Without dates, all feedback of the crew member is shown."}
        </p>
      </section>

      <MasterGrid dataSource={shown} keyExpr="id" loading={loading} onRefresh={load} exportFileName="Feedback" allowColumnChooser={false}>
        <Column {...OPEN_COLUMN} cellRender={renderOpen} />
        <Column
          dataField="createdAt"
          caption="Sent on"
          dataType="datetime"
          width={140}
          calculateDisplayValue={(r) => formatDateTime(r.createdAt)}
          sortOrder="desc"
        />
        <Column caption="Type" width={135} calculateCellValue={typeLabel} cellRender={renderType} />
        <Column dataField="airportCode" caption="Airport" width={85} />
        <Column dataField="placeName" caption="Hotel / vendor" minWidth={160} />
        <Column caption="Crew" minWidth={150} calculateCellValue={(r) => `${r.crewName} ${r.employeeNumber}`} cellRender={renderCrew} />
        <Column caption="Issue" width={170} calculateCellValue={(r) => `${r.issueType} ${r.priority}`} cellRender={renderIssue} />
        <Column dataField="description" caption="Feedback" minWidth={200} cellRender={renderText} />
        <Column caption="Status" width={140} calculateCellValue={(r) => statusOf(r).label} cellRender={renderStatus} />
        <Column
          caption="Last update"
          width={140}
          dataType="datetime"
          calculateCellValue={lastActivity}
          calculateDisplayValue={(r) => formatDateTime(lastActivity(r))}
        />
      </MasterGrid>

      {selected && <FeedbackDrawer key={selected.id} feedback={selected} canEdit={canEdit} onChanged={load} onClose={() => setSelectedId(null)} />}
    </MasterPage>
  );
}

export default FeedbackComponent;
